import ClickLogger from '@/components/common/ClickLogger';
import RequestButton from '@/components/templates/diagnosis/treatment-request/components/RequestButton';
import { EWebTreatmentRequestLog } from '@/entities/diagnosis/libs';
import { DayScheduleGroupedByTimePeriod, TreatmentRequest } from '@/models/diagnosis';
import { cn } from '@/utils/style';
import { CheckItem } from 'doctornow-design-system';
import { useEffect } from 'react';
import { checkRestrictedTimeRange } from '../lib';
import TimePeriodSection from './TimePeriodSection';

interface TelemedicineBookingDaysProps {
  treatmentRequest: TreatmentRequest;
  days: DayScheduleGroupedByTimePeriod[];
  selectedDay: DayScheduleGroupedByTimePeriod | null;
  quickestMinutes: number | null;
  selectBookingTargetDate: (bookingTargetDate: string) => void;
  selectBookingTimeSlot: (startTimeSlotValue: number, endTimeSlotValue: number) => void;
  toggleAsapRequest: () => void;
  selectQuickMode: () => Promise<void>;
  selectGeneralMode: () => void;
}

const TelemedicineBookingDays = ({
  treatmentRequest,
  days,
  quickestMinutes,
  selectedDay,
  selectBookingTargetDate,
  selectBookingTimeSlot,
  toggleAsapRequest,
  selectQuickMode,
  selectGeneralMode,
}: TelemedicineBookingDaysProps) => {
  const isQuickScheduleMode = treatmentRequest.isWebQuickScheduleRequest();
  const hasScheduleModeSelected = treatmentRequest.hasScheduleModeSelected();
  const isRestrictedTimeRange = checkRestrictedTimeRange();

  useEffect(() => {
    if (isRestrictedTimeRange && !hasScheduleModeSelected) {
      selectGeneralMode();
    }
  }, [isRestrictedTimeRange, hasScheduleModeSelected, selectGeneralMode]);

  return (
    <div className='flex flex-col gap-[20px] p-[0px_24px_32px]'>
      {!isRestrictedTimeRange && (
        <div className='flex flex-col gap-[12px]'>
          <ClickLogger params={{ eventName: EWebTreatmentRequestLog.ClickQuickScheduleMode }}>
            <button
              type='button'
              onClick={selectQuickMode}
              className={cn(
                'flex flex-col items-start gap-[2px] rounded-[8px] border border-solid p-[20px] transition-all',
                hasScheduleModeSelected && isQuickScheduleMode && 'border-primary-700 bg-[#fff9f5]',
                (!hasScheduleModeSelected || !isQuickScheduleMode) && 'border-gray-100 bg-white'
              )}
            >
              <div className='flex w-full items-center gap-[4px]'>
                <p
                  className={cn(
                    'typography-ui-15sb',
                    hasScheduleModeSelected && isQuickScheduleMode && 'text-primary-800',
                    (!hasScheduleModeSelected || !isQuickScheduleMode) && 'text-gray-900'
                  )}
                >
                  가장 빠른 시간에 진료받기
                </p>
                {quickestMinutes !== null && (
                  <div className='flex items-center justify-center rounded-[16px] bg-[#fff2ea] px-[8px] py-[2px]'>
                    <p className='typography-ui-12sb text-primary-700'>{quickestMinutes}분 내 진료</p>
                  </div>
                )}
              </div>
              <p className='typography-ui-13r text-left text-gray-500'>최대한 빠르게 진료 가능한 의사를 찾아드려요.</p>
            </button>
          </ClickLogger>

          <ClickLogger params={{ eventName: EWebTreatmentRequestLog.ClickGeneralScheduleMode }}>
            <button
              type='button'
              onClick={selectGeneralMode}
              className={cn(
                'flex flex-col items-start gap-[2px] rounded-[8px] border border-solid p-[20px] transition-all',
                hasScheduleModeSelected && !isQuickScheduleMode && 'border-primary-700 bg-[#fff9f5]',
                (!hasScheduleModeSelected || isQuickScheduleMode) && 'border-gray-100 bg-white'
              )}
            >
              <p
                className={cn(
                  'typography-ui-15sb',
                  hasScheduleModeSelected && !isQuickScheduleMode && 'text-primary-800',
                  (!hasScheduleModeSelected || isQuickScheduleMode) && 'text-gray-900'
                )}
              >
                원하는 시간에 진료받기
              </p>
              <p className='typography-ui-13r text-left text-gray-500'>
                내가 원하는 시간에 진료 가능한 의사를 찾아드려요.
              </p>
            </button>
          </ClickLogger>
        </div>
      )}

      {(isRestrictedTimeRange || (hasScheduleModeSelected && !isQuickScheduleMode)) && (
        <div className='flex items-center gap-[8px]'>
          {days.map((day) => (
            <RequestButton
              key={`day-${day.displayName}`}
              selected={selectedDay?.targetDate === day.targetDate}
              onClick={() => selectBookingTargetDate(day.targetDate)}
              text={day.displayName}
            />
          ))}
        </div>
      )}

      {!isQuickScheduleMode && selectedDay && (
        <>
          {selectedDay.isBookableDay ? (
            <div className='flex flex-col gap-[12px]'>
              <div className='flex flex-col gap-[16px]'>
                <TimePeriodSection
                  title='새벽'
                  schedules={selectedDay.dawnSchedules}
                  treatmentRequest={treatmentRequest}
                  selectBookingTimeSlot={selectBookingTimeSlot}
                  showWarning={true}
                  warningMessage='새벽 시간대에는 진료 가능한 의사가 부족할 수 있어요.'
                />
                <TimePeriodSection
                  title='오전'
                  schedules={selectedDay.morningSchedules}
                  treatmentRequest={treatmentRequest}
                  selectBookingTimeSlot={selectBookingTimeSlot}
                />
                <TimePeriodSection
                  title='오후'
                  schedules={selectedDay.afternoonSchedules}
                  treatmentRequest={treatmentRequest}
                  selectBookingTimeSlot={selectBookingTimeSlot}
                />
                <TimePeriodSection
                  title='야간'
                  schedules={selectedDay.nightSchedules}
                  treatmentRequest={treatmentRequest}
                  selectBookingTimeSlot={selectBookingTimeSlot}
                  showWarning={true}
                  warningMessage='야간 시간대에는 진료 가능한 의사가 부족할 수 있어요.'
                />
              </div>
              {treatmentRequest.shouldShowAsapRequest() && (
                <div className='flex items-center gap-[12px] px-[4px] py-[4px]'>
                  <CheckItem
                    label='예약한 시간보다 더 빨리 진료받고 싶어요'
                    checkboxProps={{
                      checked: treatmentRequest.form.isAsapRequest,
                      onCheckedChange: toggleAsapRequest,
                    }}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className='typography-ui-15m flex h-[44px] w-full items-center justify-center rounded-[8px] bg-gray-20 p-[12px] text-gray-400'>
              예약이 꽉 찼어요
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TelemedicineBookingDays;
